// Receive Meds — the pharmacy dispensing confirmation queue (2026-07-20).
//
// Lists medication the pharmacy has DELIVERED but the ward has not yet
// confirmed, and lets the nurse accept/reject each line. Confirming calls the
// real pharmacy service (confirmDispense), which consumes stock and captures
// the charge — this REPLACES the old direct order-status flip.
//
// The queue is facility-wide (the backend applies no admission/ward filter),
// so this is a shell-level screen, not a per-patient dialog. Each row is
// self-contained (patient/item/store names pre-resolved server-side).
//
// A confirm requires a decision for EVERY line of the transaction (the backend
// 422s otherwise — stock is consumed for the whole transaction at once), so the
// UI defaults every line to "accept full quantity" and the nurse adjusts.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, IconButton, InputAdornment, LinearProgress, MenuItem, Paper, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";

import { NURSE_COLORS } from "../theme";
import { useDebouncedValue } from "../hooks";
import {
  getPendingConfirmations, confirmDispense, postPharmacyChargeCapture, listOf,
} from "../api/nurseApi";
import { buildChargeCapturePayload } from "../pharmacyCharges";
import { istDayKey, formatIstTime } from "../../../utils/istDateTime";

// delivered_at is a UTC instant that arrives unmarked — slicing its raw digits
// showed every delivery 5h30m before it happened.
const fmt = (dt) => {
  const day = istDayKey(dt);
  return day ? `${day} ${formatIstTime(dt)}` : "—";
};
const num = (v) => (v == null || v === "" ? 0 : Number(v));

// Per-line editable decision, seeded from the delivered quantity.
function initialLines(txn) {
  return (txn.items || []).map((it) => ({
    line_item_id: it.line_item_id,
    // item_id (distinct from line_item_id) is what billing charges against —
    // it was dropped here, so charge capture had nothing to post.
    item_id: it.item_id,
    item_name: it.item_name || it.item_id,
    batch_number: it.batch_number,
    expiry_date: it.expiry_date || it.expiryDate || "",
    requested: num(it.requested_quantity),
    unit_rate: it.unit_rate,
    accepted: num(it.requested_quantity), // default: accept in full
    rejection_reason: "",
  }));
}

function ConfirmDialog({ txn, onClose, onConfirmed }) {
  const [lines, setLines] = useState(() => initialLines(txn));
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const setAccepted = (id, value) =>
    setLines((prev) => prev.map((l) => {
      if (l.line_item_id !== id) return l;
      const accepted = Math.max(0, Math.min(num(value), l.requested));
      return { ...l, accepted };
    }));
  const setReason = (id, value) =>
    setLines((prev) => prev.map((l) => (l.line_item_id === id ? { ...l, rejection_reason: value } : l)));

  // A rejected line (accepted < requested) needs a reason — the pharmacy
  // service records why stock came back.
  const missingReason = lines.some((l) => l.accepted < l.requested && !l.rejection_reason.trim());

  // Charge capture for the accepted lines. Isolated so a billing problem can
  // never be mistaken for a receipt problem by the catch below.
  const captureCharges = async () => {
    const admissionId = txn.admission_id || txn.admissionId;
    const payload = buildChargeCapturePayload(lines);
    if (!payload) return;                 // everything was rejected — nothing to bill
    if (!admissionId) {
      console.warn("[CHARGE_CAPTURE] Skipped — delivery has no admission_id.", txn.transaction_number);
      toast.warn("Receipt confirmed, but the patient was not billed (no admission on this delivery).");
      return;
    }
    try {
      const res = await postPharmacyChargeCapture(admissionId, payload);
      if (res?.status === "Failed") {
        toast.warn(`Receipt confirmed, but billing failed: ${res?.message || "charge not captured"}`);
      }
    } catch (err) {
      console.error("[CHARGE_CAPTURE] Failed", err);
      toast.warn("Receipt confirmed, but the pharmacy charge was not captured. Billing will need to raise it.");
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await confirmDispense(txn.transaction_id, {
        remarks: remarks.trim() || undefined,
        // A decision for EVERY line — the backend rejects a partial list.
        line_items: lines.map((l) => {
          const rejected = l.requested - l.accepted;
          return {
            line_item_id: l.line_item_id,
            accepted_quantity: String(l.accepted),
            rejected_quantity: String(rejected),
            rejection_reason: rejected > 0 ? (l.rejection_reason.trim() || null) : null,
          };
        }),
      });
      if (res?.status === "Success") {
        toast.success(`Confirmed ${txn.transaction_number}`);
        // Bill what the ward accepted. This runs AFTER the confirm and never
        // undoes it: the stock has already moved and the row has left the
        // queue server-side, so a billing failure must surface as a warning to
        // chase, not as a failed receipt the nurse would re-submit (which
        // would 422 — the transaction is no longer pending).
        await captureCharges();
        onConfirmed();
      } else {
        toast.error(res?.message || "Could not confirm receipt.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not confirm receipt.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: NURSE_COLORS.primary }}>
        Confirm receipt — {txn.transaction_number}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {[txn.patient_name, txn.store_name, `Delivered ${fmt(txn.delivered_at)}`].filter(Boolean).join(" · ")}
        </Typography>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
                <TableCell>Item</TableCell><TableCell>Batch</TableCell>
                <TableCell align="right">Delivered</TableCell>
                <TableCell align="right">Accept</TableCell>
                <TableCell align="right">Reject</TableCell>
                <TableCell>Reason (if rejecting)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((l) => {
                const rejected = l.requested - l.accepted;
                return (
                  <TableRow key={l.line_item_id}>
                    <TableCell>{l.item_name}</TableCell>
                    <TableCell>{l.batch_number || "—"}</TableCell>
                    <TableCell align="right">{l.requested}</TableCell>
                    <TableCell align="right" sx={{ width: 110 }}>
                      <TextField
                        size="small" type="number" value={l.accepted}
                        inputProps={{ min: 0, max: l.requested, style: { textAlign: "right" } }}
                        onChange={(e) => setAccepted(l.line_item_id, e.target.value)}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ color: rejected > 0 ? "error.main" : "text.disabled" }}>
                      {rejected}
                    </TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      <TextField
                        size="small" fullWidth placeholder={rejected > 0 ? "Required" : "—"}
                        disabled={rejected === 0}
                        error={rejected > 0 && !l.rejection_reason.trim()}
                        value={l.rejection_reason}
                        onChange={(e) => setReason(l.line_item_id, e.target.value)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        <TextField
          label="Remarks (optional)" fullWidth size="small" sx={{ mt: 2 }}
          value={remarks} onChange={(e) => setRemarks(e.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting} variant="outlined">Cancel</Button>
        <Button onClick={submit} disabled={submitting || missingReason} variant="contained">
          {submitting ? "Confirming…" : "Confirm receipt"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PendingConfirmations() {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null); // transaction being confirmed
  const debouncedQ = useDebouncedValue(q);

  const reload = useCallback(() => {
    setLoading(true);
    getPendingConfirmations()
      .then((r) => setTxns(listOf(r)))
      .catch(() => setTxns([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(reload, [reload]);

  const filtered = useMemo(() => {
    const term = debouncedQ.toLowerCase().trim();
    if (!term) return txns;
    return txns.filter((t) =>
      (t.patient_name || "").toLowerCase().includes(term) ||
      (t.transaction_number || "").toLowerCase().includes(term) ||
      (t.items || []).some((i) => (i.item_name || "").toLowerCase().includes(term))
    );
  }, [txns, debouncedQ]);

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { sm: "center" }, justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography variant="subtitle1">Receive Meds</Typography>
          <Typography variant="caption" color="text.secondary">
            Delivered by pharmacy · awaiting your confirmation
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <TextField
            size="small" placeholder="Search patient, order, item" value={q}
            onChange={(e) => setQ(e.target.value)}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
          />
          <Tooltip title="Refresh"><IconButton onClick={reload}><RefreshIcon /></IconButton></Tooltip>
        </Stack>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
                <TableCell>Order</TableCell><TableCell>Patient</TableCell>
                <TableCell>Store</TableCell><TableCell>Delivered</TableCell>
                <TableCell align="center">Items</TableCell><TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((t) => (
                <TableRow key={t.transaction_id} hover>
                  <TableCell>
                    {t.transaction_number}
                    {t.priority && t.priority !== "NORMAL" && (
                      <Chip size="small" color="warning" label={t.priority} sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>{t.patient_name || t.patient_id || "—"}</TableCell>
                  <TableCell>{t.store_name || "—"}</TableCell>
                  <TableCell>{fmt(t.delivered_at)}</TableCell>
                  <TableCell align="center">{t.item_count ?? (t.items || []).length}</TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="contained" onClick={() => setActive(t)}>Receive</Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    Nothing awaiting confirmation.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      {active && (
        <ConfirmDialog
          txn={active}
          onClose={() => setActive(null)}
          onConfirmed={() => { setActive(null); reload(); }}
        />
      )}
    </Box>
  );
}
